const reducedMotionQuery = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
);
const compactViewportQuery = window.matchMedia("(max-width: 799px)");
const MAX_VISIBLE_BUBBLES = 4;
const BUBBLE_HIDE_DURATION = 420;

const randomBetween = (minimum, maximum) =>
  Math.round(minimum + Math.random() * (maximum - minimum));

export const initTopMap = () => {
  const map = document.querySelector(".js-top-map");
  const mapObject = map?.querySelector(".js-top-map-object");

  if (!map || !mapObject) {
    return;
  }

  let bubbles = [];
  const bubbleTimers = new Set();
  const visibleBubbles = new Set();
  let isMapVisible = false;
  let isMapReady = false;

  const clearBubbleTimers = () => {
    for (const timer of bubbleTimers) {
      window.clearTimeout(timer);
    }

    bubbleTimers.clear();
  };

  const createTimer = (callback, delay) => {
    const timer = window.setTimeout(() => {
      bubbleTimers.delete(timer);
      callback();
    }, delay);

    bubbleTimers.add(timer);
  };

  const setBubbleVisibility = (bubble, isVisible, immediately = false) => {
    const showTransition = compactViewportQuery.matches
      ? "opacity 280ms ease-out, transform 520ms cubic-bezier(0.22, 1.35, 0.36, 1)"
      : "opacity 480ms ease-out, transform 760ms cubic-bezier(0.22, 1.35, 0.36, 1)";
    const hideTransition = compactViewportQuery.matches
      ? "opacity 260ms ease-in, transform 260ms ease-in"
      : "opacity 420ms ease-in, transform 420ms ease-in";

    bubble.style.transition = immediately
      ? "none"
      : isVisible
        ? showTransition
        : hideTransition;
    bubble.style.opacity = isVisible ? "1" : "0";
    bubble.style.transform = isVisible
      ? "translateY(0) scale(1)"
      : "translateY(10px) scale(0.78)";
  };

  const scheduleBubble = (bubble, initialDelay = 0) => {
    createTimer(() => {
      if (!isMapVisible) {
        return;
      }

      if (visibleBubbles.size >= MAX_VISIBLE_BUBBLES) {
        scheduleBubble(bubble, randomBetween(400, 1000));
        return;
      }

      visibleBubbles.add(bubble);
      setBubbleVisibility(bubble, true);

      createTimer(
        () => {
          if (!isMapVisible) {
            return;
          }

          setBubbleVisibility(bubble, false);
          createTimer(() => {
            visibleBubbles.delete(bubble);
            scheduleBubble(bubble, randomBetween(600, 1900));
          }, BUBBLE_HIDE_DURATION);
        },
        randomBetween(1900, 4200),
      );
    }, initialDelay || randomBetween(200, 1600));
  };

  const stopAnimation = () => {
    clearBubbleTimers();
    visibleBubbles.clear();

    for (const bubble of bubbles) {
      setBubbleVisibility(bubble, false, true);
    }
  };

  const startAnimation = () => {
    if (!isMapReady || !isMapVisible || compactViewportQuery.matches) {
      return;
    }

    clearBubbleTimers();

    bubbles.forEach((bubble, index) => {
      scheduleBubble(bubble, 250 + index * 90 + randomBetween(0, 900));
    });
  };

  const centerCompactMap = () => {
    if (!compactViewportQuery.matches) {
      return;
    }

    window.requestAnimationFrame(() => {
      map.scrollLeft = (map.scrollWidth - map.clientWidth) / 2;
    });
  };

  mapObject.addEventListener("load", () => {
    const mapDocument = mapObject.contentDocument;

    if (!mapDocument) {
      return;
    }

    bubbles = Array.from(mapDocument.querySelectorAll('[id^="fukidashi"]'));

    for (const bubble of bubbles) {
      bubble.style.transformBox = "fill-box";
      bubble.style.transformOrigin = "center bottom";
      setBubbleVisibility(bubble, false, true);
    }

    isMapReady = true;
    mapObject.classList.add("is-ready");
    centerCompactMap();

    if (compactViewportQuery.matches) {
      return;
    }

    if (reducedMotionQuery.matches) {
      for (const bubble of bubbles) {
        setBubbleVisibility(bubble, true, true);
      }
      return;
    }

    startAnimation();
  });

  compactViewportQuery.addEventListener("change", (event) => {
    if (!isMapReady) {
      return;
    }

    if (event.matches) {
      stopAnimation();
      centerCompactMap();
      return;
    }

    if (reducedMotionQuery.matches) {
      for (const bubble of bubbles) {
        setBubbleVisibility(bubble, true, true);
      }
      return;
    }

    startAnimation();
  });

  const loadMap = () => {
    if (!mapObject.data && mapObject.dataset.mapSrc) {
      mapObject.data = mapObject.dataset.mapSrc;
    }
  };

  if (!("IntersectionObserver" in window)) {
    isMapVisible = true;
    loadMap();
    return;
  }

  const loadObserver = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }

      loadMap();
      loadObserver.disconnect();
    },
    { rootMargin: "600px 0px" },
  );

  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      isMapVisible = entry.isIntersecting;

      if (isMapVisible) {
        startAnimation();
      } else if (isMapReady) {
        stopAnimation();
      }
    },
    { threshold: 0.15 },
  );

  loadObserver.observe(map);
  visibilityObserver.observe(map);
};
